import { Component, OnInit, Input, Output, EventEmitter } from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import { finalize } from "rxjs/operators";
import { OrdenService } from "app/services/orden.service";
import { PacienteService } from "app/services/paciente.service";
import { CentroMedicoService } from "app/services/centro-medico.service";
import { SpinnerService } from "app/services/spinner.service";
import { Orden, CrearOrdenRequest } from "app/models/orden.interface";

interface PacienteOption {
  id: number;
  name: string;
  surname: string;
  numeroDocumento: string;
  email: string;
  celPais: string;
  celCaract: string;
  celNumero: string;
}

@Component({
  selector: "app-crear-orden",
  templateUrl: "./crear-orden.component.html",
  styleUrls: ["./crear-orden.component.css"],
})
export class CrearOrdenComponent implements OnInit {
  @Input() pacienteInput: PacienteOption | null = null;
  @Input() centroMedicoIdInput: number | null = null;
  @Input() modoDialog = false;

  @Output() ordenCreada = new EventEmitter<Orden>();

  pacientes: PacienteOption[] = [];
  pacientesFiltrados: PacienteOption[] = [];
  pacienteSeleccionado: PacienteOption | null = null;
  filtroPaciente = "";

  diagnosisControl = new FormControl("", [
    Validators.required,
    Validators.maxLength(500),
  ]);

  medicineControl = new FormControl("", [
    Validators.required,
    Validators.maxLength(5000),
  ]);

  procesando = false;
  ordenGenerada: Orden | null = null;
  error = "";

  medicoId: number;
  centroMedicoId: number;

  constructor(
    private ordenService: OrdenService,
    private pacienteService: PacienteService,
    private centroMedicoService: CentroMedicoService,
    private spinnerService: SpinnerService,
  ) {}

  ngOnInit(): void {
    this.medicoId = Number(localStorage.getItem("medicoId"));

    if (!this.medicoId || isNaN(this.medicoId)) {
      this.error =
        "No se pudo identificar al médico. Por favor, inicie sesión nuevamente.";
      return;
    }

    if (this.pacienteInput) {
      this.pacienteSeleccionado = this.pacienteInput;
    } else {
      this.cargarPacientes();
    }

    if (this.centroMedicoIdInput) {
      this.centroMedicoId = this.centroMedicoIdInput;
    } else {
      this.cargarCentroMedico();
    }
  }

  cargarCentroMedico(): void {
    this.centroMedicoService.mostrarCentroMedico().subscribe({
      next: (data: any) => {
        this.centroMedicoId = data.id;
      },
      error: () => {
        this.centroMedicoId = 0;
      },
    });
  }

  cargarPacientes(): void {
    this.pacienteService.listarPacientesDeMedico(this.medicoId).subscribe({
      next: (data: any) => {
        this.pacientes = (data.pacientes || data || []).map((p: any) => ({
          id: p.id,
          name: p.name || "",
          surname: p.surname || "",
          numeroDocumento: p.numeroDocumento || "",
          email: p.email || "",
          celPais: p.celPais || "",
          celCaract: p.celCaract || "",
          celNumero: p.celNumero || "",
        }));
      },
      error: () => {
        this.pacientes = [];
      },
    });
  }

  filtrarPacientes(): void {
    const filtro = this.filtroPaciente.toLowerCase().trim();
    if (!filtro) {
      this.pacientesFiltrados = [];
      return;
    }
    this.pacientesFiltrados = this.pacientes.filter(
      (p) =>
        p.name.toLowerCase().includes(filtro) ||
        p.surname.toLowerCase().includes(filtro) ||
        p.numeroDocumento.includes(filtro),
    );
  }

  seleccionarPaciente(paciente: PacienteOption): void {
    this.pacienteSeleccionado = paciente;
    this.filtroPaciente = "";
    this.pacientesFiltrados = [];
  }

  limpiarPaciente(): void {
    this.pacienteSeleccionado = null;
  }

  formularioValido(): boolean {
    return (
      !!this.pacienteSeleccionado &&
      !!this.centroMedicoId &&
      this.diagnosisControl.valid &&
      this.medicineControl.valid
    );
  }

  crearOrden(): void {
    if (!this.formularioValido()) {
      this.diagnosisControl.markAsTouched();
      this.medicineControl.markAsTouched();
      const faltan: string[] = [];
      if (!this.pacienteSeleccionado) faltan.push("Paciente");
      if (!this.centroMedicoId) faltan.push("Centro médico");
      if (this.diagnosisControl.invalid) faltan.push("Diagnóstico");
      if (this.medicineControl.invalid) faltan.push("Medicación e indicaciones");
      this.error = faltan.length
        ? "Completá los campos obligatorios: " + faltan.join(", ")
        : "Revisá los datos marcados en el formulario";
      return;
    }

    this.procesando = true;
    this.error = "";
    this.ordenGenerada = null;
    this.spinnerService.mostrarSpinner();

    const request: CrearOrdenRequest = {
      paciente_id: this.pacienteSeleccionado!.id,
      centro_medico_id: this.centroMedicoId,
      diagnosis: this.diagnosisControl.value!,
      medicine: this.medicineControl.value!,
    };

    this.ordenService
      .crear(request)
      .pipe(
        finalize(() => {
          this.procesando = false;
          this.spinnerService.ocultarSpinner();
        }),
      )
      .subscribe({
        next: (orden) => {
          this.ordenGenerada = orden;
          this.ordenCreada.emit(orden);
        },
        error: (err) => {
          this.error = err.error?.error || "Error al crear la orden";
        },
      });
  }

  nuevaOrden(): void {
    this.pacienteSeleccionado = this.pacienteInput ?? null;
    this.diagnosisControl.reset();
    this.medicineControl.reset();
    this.ordenGenerada = null;
    this.error = "";
  }
}
